import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VideocamIcon from '@mui/icons-material/Videocam';
import PushPinIcon from '@mui/icons-material/PushPin';
import { useOnClickOutside } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import { selectShareComposer, switchShareModalComposer } from '../../../store/composers/composersSlice';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { NOTIFICATION, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { postVideofeedViewRequest, updateVideofeedTitle } from '../../../api';
import { ACCESS_TYPE, ACCESS_TYPE_KEYS } from '../../../enums/ACCESS_ENUM';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { clearVideoFeeds } from '../../../store/videos/videoSlice';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import { MoreIcon } from '../../../assets/icons/threedots';
import DownloadButton from '../../modules/DownloadButton';
import DeleteComposerModal from '../DeleteComposerModal';
import Icon from '../../VideofeedsList/Videofeed/icon';
import NotReady from '../../../assets/icons/not-ready';
import ShareButton from '../../modules/ShareButton';
import TrashButton from '../../modules/TrashButton';
import EditButton from '../../modules/EditButton';
import { VIDEOFEED } from '../../../enums/TYPES';
import { bytesToMB } from '../../../helpers';
import { Portal } from '../../../helpers/bodyPortal';
import { useOverflowAwareActionMenu } from '../../../helpers/useOverflowAwareActionMenu';
import { PATHS } from '../../../enums/PATHS';
import styles from './Composer.module.scss';
import { Tooltip } from '@material-ui/core';
import { openDownloadManager } from '../../../store/downloadManager/downloadManagerSlice';
import { RolesLowerCase } from '../../../enums/ROLES';
import { SortOptions } from '../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../modules/DateSortHeader';
import FileTypeThumbnail from '../../modules/FileTypeThumbnail';
import ReplaceThumbnailModal from '../../modules/ReplaceThumbnailModal';
import { removeVideofeedThumbnail, uploadVideofeedThumbnail } from '../../../api/videofeeds';
import ThumbnailButton from '../../modules/ThumbnailButton';

type TComposer = {
  composer: VIDEOFEED;
  owner?: boolean;
  feedIndex?: number;
  sharedComposer?: boolean;
  noActions?: boolean;
  pinContextType?: string;
  pinContextParentId?: string | number | null;
  onPinUpdated?: () => void;
  dateSortOption?: SortOptions | null;
};

const Composer: React.FC<TComposer> = ({
  composer,
  owner = true,
  feedIndex,
  sharedComposer,
  noActions,
  pinContextType,
  pinContextParentId,
  onPinUpdated,
  dateSortOption,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const [title, setTitle] = useState(composer.title);
  const [isEditing, setIsEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const notification = useSnackbar();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(composer.thumbnail_url || '');
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({
    showMenu: showDotsMenu,
    anchorPosition: menuAnchorPosition,
  });
  const switchDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const currentOrgUser =
    currentUser?.organization_users?.find((orgUser) => orgUser.organization?.id === myOrganization?.id) ||
    currentUser?.organization_users?.[0];
  const organizationHaveDownloadHours = Number(myOrganization?.download_hours_limit || 0) > 0;
  const allowDownloadOwnComposers = currentOrgUser?.allow_download_own_composers ?? true;
  const isViewerOrAnalyst = [RolesLowerCase.VIEWER, RolesLowerCase.ANALYST].includes(currentUser?.role?.title as RolesLowerCase);
  const isOwnMyComposer = composer?.type === VIDEOFEED_TYPE.MY_COMPOSER && composer?.created_by?.id === currentUser?.id;
  const canDownloadOwnMyComposer = !isViewerOrAnalyst || allowDownloadOwnComposers;
  const canDownload = organizationHaveDownloadHours && (!isOwnMyComposer || canDownloadOwnMyComposer);
  const previewUrl = thumbnailUrl || composer?.videos?.[0]?.s3_preview_url || '';
  const isPinned = Boolean(composer?.is_pinned);

  useEffect(() => {
    setThumbnailUrl(composer.thumbnail_url || '');
  }, [composer.thumbnail_url]);

  const onDownload = (e: any) => {
    e.stopPropagation();
    if (!canDownload) return;
    setShowDotsMenu(false);
    openComposerDownload();
  };

  const openComposerDownload = () => {
    dispatch(
      openDownloadManager({
        feedId: String(composer.id),
        feedTitle: composer.title,
      }),
    );
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    setMenuAnchorPosition(null);
  });

  const handleDeleteClick = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setShowDotsMenu(false);
    setShowDeleteModal(true);
  };

  const onShareClick = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    dispatch(selectShareComposer(composer));
    dispatch(switchShareModalComposer(true));
    setShowDotsMenu(false);
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  const onPinToggle = async (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      const { pinContent, unpinContent } = await import('../../../api/pinnedContentApi');
      if (isPinned) {
        await unpinContent({
          targetType: 'videofeed',
          targetId: composer.id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      } else {
        await pinContent({
          targetType: 'videofeed',
          targetId: composer.id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      }
      onPinUpdated?.();
      notification.showMessage(isPinned ? 'Unpinned' : 'Pinned');
      setShowDotsMenu(false);
    } catch {
      notification.showMessage('Failed to update pin');
    }
  };

  type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;
  const redirectToVideoPage = (e: CustomEventHandler) => {
    if (isEditing) return;
    if (noActions) return;
    if (e.currentTarget.contains(e.target as Node)) {
      postVideofeedViewRequest(composer.id);
      history.push(
        `${PATHS.COMPOSER_PAGE.path}?feedId=${composer.id}&fileside=true&feed=${feedIndex}&clipId=${
          composer.videos[0].id
        }&clip=0&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}${sharedComposer ? '&sharedComposer=true' : ''}`,
      );
    }
    dispatch(clearVideoFeeds());
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }
    if (e.type === 'contextmenu') {
      setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    } else {
      setMenuAnchorPosition(null);
    }
    setShowDotsMenu(true);
  };

  const canShare = (): boolean => {
    const result =
      !!composer?.access?.filter(
        (el) => el.access.title === ACCESS_TYPE_KEYS[ACCESS_TYPE.EDIT_AND_RE_SHARE] && el?.user?.id === currentUser?.id,
      ).length || composer.created_by?.id === currentUser?.id;
    return result;
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const onHover = () => {
    setHover(!hover);
  };

  const handleEdit = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (noActions) return;
    if (title.trim().length === 0) {
      setTitle(composer.title);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);
    await updateVideofeedTitle(composer.id, title);
    setHover(false);
    setShowDotsMenu(false);

    if (composer.title !== title) window.location.reload();

    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const composerSize = useMemo(() => {
    const size = composer?.size ? composer.size : composer?.videos?.reduce((a, b) => Number(a) + Number(b.size), 0);

    if (!isNaN(size)) {
      return size;
    } else {
      return 0;
    }
  }, [composer]);

  return (
    <div
      style={{
        position: 'relative',
        boxShadow: isPinned ? 'inset 0 0 0 1px rgba(57, 179, 234, 0.35)' : undefined,
      }}
      className={cx(styles.container, showDotsMenu && styles.selected)}
      onClick={redirectToVideoPage}
      onContextMenu={switchDotsMenu}
    >
      <div className={styles.link} onClick={redirectToVideoPage}>
        <div className={styles.leftSide} onClick={redirectToVideoPage}>
          <FileTypeThumbnail
            className={styles.fileTypeThumbnail}
            icon={
              <Icon onClick={redirectToVideoPage}>
                <ComposerIcon
                  className={styles.composerIcon}
                  style={{ pointerEvents: 'none', color: composer?.type === VIDEOFEED_TYPE.MY_COMPOSER ? '#39b3ea' : undefined }}
                  onClick={redirectToVideoPage}
                />
              </Icon>
            }
          >
            {previewUrl ? (
              <img src={previewUrl} alt={'video feed'} className={styles.thumbnail} />
            ) : composer?.videos?.length > 0 ? (
              <VideocamIcon className={styles.thumbnail} />
            ) : (
              <NotReady className={styles.thumbnail} />
            )}
          </FileTypeThumbnail>
          <div />
          {isEditing ? (
            <form
              onSubmit={handleEdit}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <input ref={inputRef} className={styles.input} onChange={handleChange} defaultValue={composer.title} onBlur={handleEdit} />
            </form>
          ) : (
            <div className={styles.name}>
              <span>{composer.title}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.rightSide}>
        <div className={styles.owner}>{composer?.created_by?.username}</div>
        <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(composer, dateSortOption), true)}`}>
          <div className={styles.date}>{DateFormatter(getDateColumnValue(composer, dateSortOption), true)}</div>
        </Tooltip>
        <div className={styles.size}>
          <img src={StorageIcon} alt="storage" />
          {bytesToMB(composerSize, 0) || 1} MB
        </div>

        {!noActions && (
          <div className={styles.dotsMenuBtn}>
            <div ref={triggerRef} className={styles.dotIcon} onClick={switchDotsMenu}>
              <MoreIcon />
            </div>
          </div>
        )}
      </div>
      <Portal>
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            display: showDotsMenu && !noActions && composer?.created_by ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
            right: 'auto',
          }}
        >
          <div className={styles.menuOption} onClick={handleEdit}>
            <EditButton /> Rename
          </div>
          {canShare() && (
            <>
              <div className={styles.menuOption} onClick={onShareClick}>
                <ShareButton /> Share
              </div>
              <div className={styles.menuOption} onClick={onReplaceThumbnail}>
                <ThumbnailButton /> Replace Thumbnail Image
              </div>
            </>
          )}

          {canDownload && (
            <div className={styles.menuOption} onClick={onDownload}>
              <DownloadButton /> Download
            </div>
          )}
          {pinContextType && (
            <div className={styles.menuOption} onClick={onPinToggle}>
              <span
                style={{
                  display: 'inline-flex',
                  width: 32,
                  height: 30,
                  padding: 4,
                  borderRadius: 'var(--ui-border-radius)',
                  background: 'var(--action-menu-icon-bg)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 12,
                  boxSizing: 'border-box',
                }}
              >
                <PushPinIcon sx={{ fontSize: 20, color: '#fff' }} />
              </span>
              {isPinned ? 'Unpin' : 'Pin'}
            </div>
          )}

          {owner && (
            <div className={styles.menuOption} onClick={handleDeleteClick}>
              <TrashButton /> Delete
            </div>
          )}
        </div>
      </Portal>
      <ReplaceThumbnailModal
        open={showThumbnailModal}
        setOpen={setShowThumbnailModal}
        onUpload={(data) => uploadVideofeedThumbnail(composer.id, data)}
        onUploaded={(updatedComposer) => setThumbnailUrl(updatedComposer.thumbnail_url || '')}
        onRestore={thumbnailUrl ? () => removeVideofeedThumbnail(composer.id) : undefined}
        preview={{ type: 'file', title, imageUrl: previewUrl }}
      />
      <DeleteComposerModal switchModal={switchDeleteModal} open={showDeleteModal} composerId={composer.id} />
    </div>
  );
};

export default Composer;
